import { apiClient, type Paginated } from '@/core/api';

export type PDPStatus = 'active' | 'completed';
export type GoalStatus = 'open' | 'in_progress' | 'done';

export interface PDPGoal {
  id: number;
  pdp: number;
  title: string;
  description: string;
  status: GoalStatus;
  due_date: string | null;
  order: number;
  created: string;
  modified: string;
}

export interface PDPGoalInput {
  pdp: number;
  title: string;
  description?: string;
  status?: GoalStatus;
  due_date?: string | null;
  order?: number;
}

export interface PDP {
  id: number;
  /** Opaque UUIDv7 the SPA links by, so int PKs stay out of URLs (ADR-041). */
  public_id: string;
  employee: number;
  title: string;
  period: string;
  status: PDPStatus;
  summary: string;
  goals: PDPGoal[];
  created: string;
  modified: string;
}

export interface PDPInput {
  employee: number;
  title: string;
  period: string;
  status: PDPStatus;
  summary: string;
  /** Goals seeded on create (ignored on update — edited on the detail page). */
  goals?: { title: string; status?: GoalStatus }[];
}

export interface Skill {
  id: number;
  name: string;
  category: string;
  created: string;
  modified: string;
}

export interface SkillInput {
  name: string;
  category: string;
}

export interface EmployeeSkill {
  id: number;
  employee: number;
  skill: number;
  level: number;
  created: string;
  modified: string;
}

export interface EmployeeSkillInput {
  employee: number;
  skill: number;
  level: number;
}

/** PDP list endpoint — exported so list pages can build the CSV-export URL. */
export const PDPS_BASE = '/hrm/pdps/';
const PDP_GOALS = '/hrm/pdp-goals/';
const SKILLS = '/hrm/skills/';
const EMPLOYEE_SKILLS = '/hrm/employee-skills/';

// PDPs
export interface PDPListParams {
  page?: number;
  employee?: number;
  status?: PDPStatus;
  search?: string;
  /** DRF `OrderingFilter` value, e.g. `title` or `-period`. */
  ordering?: string;
}

export async function listPdps(params: PDPListParams = {}): Promise<Paginated<PDP>> {
  const { data } = await apiClient.get<Paginated<PDP>>(PDPS_BASE, { params });
  return data;
}

export async function getPdp(id: string | number): Promise<PDP> {
  const { data } = await apiClient.get<PDP>(`${PDPS_BASE}${id}/`);
  return data;
}

export async function createPdp(input: PDPInput): Promise<PDP> {
  const { data } = await apiClient.post<PDP>(PDPS_BASE, input);
  return data;
}

export async function updatePdp(id: string | number, input: Partial<PDPInput>): Promise<PDP> {
  const { data } = await apiClient.patch<PDP>(`${PDPS_BASE}${id}/`, input);
  return data;
}

export async function deletePdp(id: string | number): Promise<void> {
  await apiClient.delete(`${PDPS_BASE}${id}/`);
}

export async function createPdpGoal(input: PDPGoalInput): Promise<PDPGoal> {
  const { data } = await apiClient.post<PDPGoal>(PDP_GOALS, input);
  return data;
}

export async function updatePdpGoal(id: number, input: Partial<PDPGoalInput>): Promise<PDPGoal> {
  const { data } = await apiClient.patch<PDPGoal>(`${PDP_GOALS}${id}/`, input);
  return data;
}

export async function deletePdpGoal(id: number): Promise<void> {
  await apiClient.delete(`${PDP_GOALS}${id}/`);
}

// Skills
export async function listSkills(): Promise<Paginated<Skill>> {
  const { data } = await apiClient.get<Paginated<Skill>>(SKILLS);
  return data;
}

export async function createSkill(input: SkillInput): Promise<Skill> {
  const { data } = await apiClient.post<Skill>(SKILLS, input);
  return data;
}

export async function deleteSkill(id: number): Promise<void> {
  await apiClient.delete(`${SKILLS}${id}/`);
}

// EmployeeSkills (the matrix)
export interface EmployeeSkillListParams { page?: number; employee?: number; skill?: number }

export async function listEmployeeSkills(
  params: EmployeeSkillListParams = {},
): Promise<Paginated<EmployeeSkill>> {
  const { data } = await apiClient.get<Paginated<EmployeeSkill>>(EMPLOYEE_SKILLS, { params });
  return data;
}

export async function createEmployeeSkill(input: EmployeeSkillInput): Promise<EmployeeSkill> {
  const { data } = await apiClient.post<EmployeeSkill>(EMPLOYEE_SKILLS, input);
  return data;
}

export async function updateEmployeeSkill(
  id: number,
  input: Partial<EmployeeSkillInput>,
): Promise<EmployeeSkill> {
  const { data } = await apiClient.patch<EmployeeSkill>(`${EMPLOYEE_SKILLS}${id}/`, input);
  return data;
}

export async function deleteEmployeeSkill(id: number): Promise<void> {
  await apiClient.delete(`${EMPLOYEE_SKILLS}${id}/`);
}
